
import React from 'react';
import { Button, Card, Badge } from '../components/UI';
import { CheckCircle, Share2, Wand2, Layout, Zap, Layers, Lock } from 'lucide-react';
import { PRICING, JOISST_API_URL, APP_URL } from '../constants';

interface LandingPageProps {
  onLogin: () => void;
  onSignup: (type: 'agency' | 'user') => void;
}

export const LandingPage: React.FC<LandingPageProps> = () => {
  // Changelog data
  const features = [
    { version: 'v1.2.0', title: 'Nano Banana AI', desc: 'Added keyword highlighting and context-aware background generation.', date: 'Today' },
    { version: 'v1.1.5', title: 'Agency Portal', desc: 'Stripe Connect integration and custom client pricing enabled.', date: 'Yesterday' },
    { version: 'v1.1.0', title: 'Smart Grid', desc: '8-column grid system with 1080p and 4K responsive scaling.', date: 'Last Week' },
    { version: 'v1.0.0', title: 'Secure Launch', desc: 'Password-protected public sharing views.', date: '2 Weeks Ago' }
  ];

  const handleAuthRedirect = () => {
    // Redirect to Central Auth
    const returnTo = encodeURIComponent(APP_URL + '/');
    window.location.href = `${JOISST_API_URL}/?returnTo=${returnTo}`;
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-brand-text bg-brand-background/30">
      
      {/* 1. HERO SECTION */}
      <header className="relative overflow-hidden pt-20 pb-32">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 bg-white p-1 pr-4 rounded-full shadow-sm border border-brand-text/5 animate-fade-in-up">
              <Badge>New</Badge>
              <span className="text-sm font-semibold text-brand-text/70">Generative Brand Mockups</span>
            </div>
            
            <h1 className="text-6xl md:text-8xl font-bold tracking-tight leading-[0.9]">
              Manage Brand. <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary">
                Generate Awe.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-brand-text/60 max-w-2xl mx-auto leading-relaxed">
              The only asset manager that organizes your files on an intelligent grid and uses AI to generate on-brand marketing materials instantly.
            </p>

            <div className="flex flex-col md:flex-row gap-4 justify-center items-center pt-8">
              <Button onClick={handleAuthRedirect} className="px-10 py-5 text-xl h-auto rounded-2xl shadow-xl shadow-brand-primary/20 hover:scale-105 transition-transform">
                Get Started
              </Button>
              <Button variant="outline" onClick={handleAuthRedirect} className="px-10 py-5 text-xl h-auto rounded-2xl border-2 hover:bg-white">
                Log In
              </Button>
            </div>
            
            <div className="pt-8 text-sm text-brand-text/40 font-semibold uppercase tracking-widest">
              Trusted by Designers, Agencies & Brand Owners
            </div>
          </div>
        </div>

        {/* Abstract Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-brand-secondary/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-brand-primary/10 rounded-full blur-[100px]"></div>
        </div>
      </header>


      {/* 2. THE SMART GRID FEATURE */}
      <section className="py-24 bg-white border-y border-brand-text/5">
        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="w-16 h-16 bg-brand-background rounded-2xl flex items-center justify-center text-brand-primary mb-6">
              <Layout size={32} />
            </div>
            <h2 className="text-4xl font-bold">The 8-Column Smart Grid</h2>
            <p className="text-lg text-brand-text/70 leading-relaxed">
              Forget cluttered folders. Our intelligent dashboard organizes your assets into a fluid 8-column layout designed specifically for modern digital formats.
            </p>
            <ul className="space-y-4 pt-4">
              <li className="flex items-center gap-4">
                <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
                <span className="font-semibold">1080x1080 (Square)</span>
                <span className="text-sm text-brand-text/50">– Perfect for Instagram & Feeds (1x1 to 8x8)</span>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-2 h-2 bg-brand-secondary rounded-full"></div>
                <span className="font-semibold">1920x1080 (Landscape)</span>
                <span className="text-sm text-brand-text/50">– YouTube & Headers (2x1 to 8x4)</span>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-2 h-2 bg-brand-accent rounded-full"></div>
                <span className="font-semibold">1080x1920 (Portrait)</span>
                <span className="text-sm text-brand-text/50">– Stories & TikToks (1x2 to 4x8)</span>
              </li>
            </ul>
          </div>
          <div className="relative">
             <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary/20 to-transparent rounded-3xl transform rotate-3 scale-105"></div>
             <div className="bg-brand-background p-6 rounded-3xl shadow-2xl grid grid-cols-4 gap-4 relative z-10">
                <div className="col-span-2 row-span-2 bg-white rounded-xl shadow-sm aspect-square flex items-center justify-center text-brand-text/20 font-bold">2x2</div>
                <div className="col-span-2 row-span-1 bg-brand-primary/10 rounded-xl shadow-sm aspect-[2/1]"></div>
                <div className="col-span-1 row-span-1 bg-white rounded-xl shadow-sm aspect-square"></div>
                <div className="col-span-1 row-span-1 bg-white rounded-xl shadow-sm aspect-square"></div>
                <div className="col-span-4 row-span-2 bg-brand-text/5 rounded-xl shadow-sm aspect-[2/1] flex items-center justify-center text-brand-text/40">1920x1080 Header</div>
             </div>
          </div>
        </div>
      </section>


      {/* 3. AI GENERATION SECTION */}
      <section className="py-24 bg-brand-text text-brand-background overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-brand-primary/20 to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <Badge color="bg-brand-accent text-brand-text">Powered by Nano Banana</Badge>
            <h2 className="text-5xl font-bold mt-6 mb-8 text-white">Generative Brand Intelligence</h2>
            <p className="text-xl text-white/70 mb-12 leading-relaxed">
              Never search for a stock photo again. Click 'Generate' and watch as our <strong>Nano Banana</strong> engine creates stunning, on-brand product mockups and backgrounds using your exact color palette.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/10 border-white/10 text-white backdrop-blur-sm p-8">
              <Zap className="w-10 h-10 text-brand-secondary mb-4" />
              <h3 className="text-xl font-bold mb-2">Context Aware</h3>
              <p className="text-white/60 text-sm">The AI understands your brand colors ('#260602', '#f63b23') and integrates them into every generated pixel.</p>
            </Card>
            <Card className="bg-white/10 border-white/10 text-white backdrop-blur-sm p-8">
              <Layers className="w-10 h-10 text-brand-accent mb-4" />
              <h3 className="text-xl font-bold mb-2">Logo Overlay</h3>
              <p className="text-white/60 text-sm">Upload your SVG once. We automatically composite it onto 3D surfaces, textures, and lifestyle scenes.</p>
            </Card>
            <Card className="bg-white/10 border-white/10 text-white backdrop-blur-sm p-8">
              <Wand2 className="w-10 h-10 text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Keyword Highlights</h3>
              <p className="text-white/60 text-sm">Simply click keywords like "Office", "Nature", or "Minimal" to steer the creative direction instantly.</p>
            </Card>
          </div>
        </div>
      </section>


      {/* 4. SHARING & SECURITY */}
      <section className="py-24 bg-brand-background/50">
        <div className="container mx-auto px-6 text-center">
           <h2 className="text-4xl font-bold mb-16">Share Securely. Sleep Soundly.</h2>
           
           <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white p-8 rounded-3xl shadow-lg text-left border-l-8 border-brand-primary">
                 <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold">Public Portal</h3>
                    <Share2 className="text-brand-primary" />
                 </div>
                 <p className="text-brand-text/70 mb-6">
                   Send a single link to partners or press. They see a beautiful, read-only version of your brand grid where they can download assets in any format.
                 </p>
                 <div className="bg-brand-background p-4 rounded-xl flex items-center gap-3 text-sm font-mono text-brand-text/60">
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    brandgrid.app/u/your-brand
                 </div>
              </div>

              <div className="bg-white p-8 rounded-3xl shadow-lg text-left border-l-8 border-brand-text">
                 <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold">Asset Protection</h3>
                    <Lock className="text-brand-text" />
                 </div>
                 <p className="text-brand-text/70 mb-6">
                   Not ready for the world to see your new logo? Toggle "Password Protect" on specific assets to hide them from public view or require a pin.
                 </p>
                 <div className="flex gap-2">
                    <span className="px-3 py-1 bg-brand-text text-white rounded text-sm flex items-center gap-1"><Lock size={12}/> Confidential</span>
                    <span className="px-3 py-1 bg-brand-text/5 text-brand-text rounded text-sm flex items-center gap-1">Internal Only</span>
                 </div>
              </div>
           </div>
        </div>
      </section>


      {/* 5. AGENCY PARTNER SECTION */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
           <div className="bg-brand-text rounded-[3rem] p-8 md:p-16 text-white relative overflow-hidden">
             {/* Decorative */}
             <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary rounded-full blur-[80px] opacity-30"></div>
             
             <div className="grid md:grid-cols-2 gap-12 relative z-10 items-center">
                <div>
                   <Badge color="bg-white text-brand-text mb-6">For Agencies & Designers</Badge>
                   <h2 className="text-4xl md:text-5xl font-bold mb-6">Scale Your Operations.</h2>
                   <p className="text-xl text-white/80 mb-8">
                     Stop handing over ZIP files and walking away. Give your clients a living brand home, and manage them all from one central dashboard.
                   </p>
                   
                   <ul className="space-y-4 mb-8">
                     <li className="flex items-center gap-3 text-lg">
                       <CheckCircle className="text-brand-accent"/> 
                       <span>Centralized Client Management</span>
                     </li>
                     <li className="flex items-center gap-3 text-lg">
                       <CheckCircle className="text-brand-accent"/> 
                       <span>One-Click Asset Deployment</span>
                     </li>
                     <li className="flex items-center gap-3 text-lg">
                       <CheckCircle className="text-brand-accent"/> 
                       <span>Wholesale Platform Access</span>
                     </li>
                   </ul>

                   <Button onClick={handleAuthRedirect} variant="primary" className="bg-white text-brand-text hover:bg-brand-secondary px-8 py-4 text-lg">
                     Start Agency Account
                   </Button>
                </div>

                <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/10">
                   <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                     <span className="font-bold">Wholesale Pricing</span>
                     <span className="text-sm opacity-50">Per Account</span>
                   </div>
                   
                   <div className="space-y-6">
                     <div className="flex justify-between items-center">
                        <div>
                          <div className="font-bold text-lg">Standard User</div>
                          <div className="text-sm opacity-60">Asset hosting & Grid</div>
                        </div>
                        <div className="text-2xl font-bold">€{PRICING.wholesale.basic}</div>
                     </div>
                     
                     <div className="flex justify-between items-center">
                        <div>
                          <div className="font-bold text-lg text-brand-secondary">AI Power User</div>
                          <div className="text-sm opacity-60">Includes Nano Banana Gen</div>
                        </div>
                        <div className="text-2xl font-bold text-brand-secondary">€{PRICING.wholesale.ai}</div>
                     </div>
                   </div>
                </div>
             </div>
           </div>
        </div>
      </section>


      {/* 6. RETAIL PRICING (For Direct Users) */}
      <section className="py-24" id="pricing">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Simple Pricing</h2>
            <p className="text-xl text-brand-text/60">Manage your own brand? Pick the plan that fits.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-white p-8 rounded-3xl border border-brand-text/5 hover:shadow-xl transition-all">
               <h3 className="text-2xl font-bold mb-2">Standard</h3>
               <p className="text-brand-text/60 mb-6 h-12">Perfect for static brand guidelines and file hosting.</p>
               <div className="flex items-baseline gap-1 mb-8">
                 <span className="text-5xl font-bold tracking-tight">€{PRICING.retail.basic}</span>
                 <span className="text-brand-text/40">/mo</span>
               </div>
               <Button variant="outline" onClick={handleAuthRedirect} className="w-full mb-8 py-3">Get Started</Button>
               <ul className="space-y-3">
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-primary"/> Smart Grid Layout</li>
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-primary"/> Public Share Links</li>
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-primary"/> SVG & Format Conversion</li>
               </ul>
            </div>

            {/* AI Plan */}
            <div className="bg-brand-text text-white p-8 rounded-3xl border border-brand-text hover:shadow-2xl transition-all transform scale-105 relative">
               <div className="absolute top-0 right-0 bg-brand-primary text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-2xl">RECOMMENDED</div>
               <h3 className="text-2xl font-bold mb-2 text-brand-secondary">AI Pro</h3>
               <p className="text-white/60 mb-6 h-12">Unleash the power of Nano Banana generative mockups.</p>
               <div className="flex items-baseline gap-1 mb-8">
                 <span className="text-5xl font-bold tracking-tight">€{PRICING.retail.ai}</span>
                 <span className="text-white/40">/mo</span>
               </div>
               <Button onClick={handleAuthRedirect} className="w-full mb-8 py-3 bg-brand-secondary text-brand-text hover:bg-white">Get AI Pro</Button>
               <ul className="space-y-3">
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-secondary"/> <strong>Everything in Standard</strong></li>
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-secondary"/> Unlimited AI Generations</li>
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-secondary"/> Mockup Context Engine</li>
                 <li className="flex gap-2"><CheckCircle size={18} className="text-brand-secondary"/> Priority Support</li>
               </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. LIVE CHANGELOG */}
      <section className="bg-white border-t border-brand-text/5 py-16">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <h2 className="text-xl font-bold uppercase tracking-widest text-brand-text/50">Live Updates</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
             {features.map((f, i) => (
                <div key={i} className="group relative pl-6 border-l-2 border-brand-text/10 hover:border-brand-primary transition-colors">
                  <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-brand-text/10 group-hover:bg-brand-primary transition-colors"></div>
                  <div className="text-xs font-mono text-brand-primary mb-1">{f.version} • {f.date}</div>
                  <h4 className="font-bold text-sm mb-1">{f.title}</h4>
                  <p className="text-xs text-brand-text/60">{f.desc}</p>
                </div>
             ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-brand-text text-white py-12 border-t border-white/10">
        <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <div className="font-bold text-2xl mb-2">BrandGrid</div>
            <p className="text-white/40 text-sm max-w-xs">The modern asset manager for agencies and brands. Built with ❤️ and SOLID principles.</p>
          </div>
          <div className="flex gap-6 text-sm text-white/60">
             <a href="#" className="hover:text-white">Privacy</a>
             <a href="#" className="hover:text-white">Terms</a>
             <button onClick={handleAuthRedirect} className="hover:text-white">Login</button>
          </div>
        </div>
      </footer>
    </div>
  );
};